import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, ChevronRight, Clock, House, Info, Search, ShieldAlert, Stamp, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';
import {
  ADMIN_ROOT, ADMIN_SECTIONS, findSection, findSetting, sectionPath, settingMatches, settingPath,
  visibleAdminSettings, type AdminSetting,
} from './adminRegistry';
import { AdminLink, navigateAdmin, useAdminPath, useAdminRoute } from './adminRoute';
import { ATTENTION_PILL, attentionBySetting, useAdminAttention, type AttentionItem } from './useAdminAttention';
import UsersRolesTab from './users/UsersRolesTab';
import AilmentTemplatesTab from './ailments/AilmentTemplatesTab';
import AIAssistantRolesTab from './ai-role/AIAssistantRolesTab';
import PatientMergesTab from './merges/PatientMergesTab';
import BranchesTab from './branches/BranchesTab';
import PharmacyStoresTab from './pharmacy-stores/PharmacyStoresTab';
import WardsBedsTab from './wards/WardsBedsTab';
import SpecialtiesTab from './specialties/SpecialtiesTab';
import DoctorAvailabilityTab from './availability/DoctorAvailabilityTab';
import StorageSettingsTab from './storage/StorageSettingsTab';
import IntakeFormConfiguratorTab from './intake/IntakeFormConfiguratorTab';
import AISettingsTab from './ai-settings/AISettingsTab';
import BrandingSettingsTab from './branding/BrandingSettingsTab';
import BillingSettingsTab from './billing-settings/BillingSettingsTab';
import OpdFeeScheduleTab from './opd-fees/OpdFeeScheduleTab';
import PharmacyTab from './pharmacy-loyalty/PharmacyTab';
import DesignSystemTab from './design-system/DesignSystemTab';
import DrugReviewTab from './drug-review/DrugReviewTab';
import DrugMasterTab from './drug-master/DrugMasterTab';
import LabMastersTab from './laboratory/LabMastersTab';
import FormularyTab from './formulary/FormularyTab';
import InsuranceClaimsTab from './insurance-claims/InsuranceClaimsTab';
import BackgroundJobsTab from './background-jobs/BackgroundJobsTab';
import SmsGatewayTab from './sms-gateway/SmsGatewayTab';
import AICallLogTab from './ai-call-log/AICallLogTab';
import AIActivityTab from './ai-activity/AIActivityTab';
import DataPrivacyTab from './data-privacy/DataPrivacyTab';
import ConsentFormsTab from './consent/ConsentFormsTab';
import ReferenceLibraryTab from './clinical-library/ReferenceLibraryTab';
import EmergencyAdminTab from './emergency/EmergencyAdminTab';
import QueuePriorityTab from './queue-priority/QueuePriorityTab';
import FormCustomizationTab from './form-customization/FormCustomizationTab';

interface AdminModuleProps {
  onAiSaved?: (cfg: unknown) => void;
}

/**
 * The Admin Panel shell — docs/design/admin-panel/Admin_Panel_Wireframe.html,
 * phase 1. Every level is a page with its own URL:
 *
 *   /admin                          Admin home: recently used + every section
 *   /admin/<section>                that section's settings, one row each
 *   /admin/<section>/<setting>      the setting itself
 *
 * so the topbar breadcrumb (AdminBreadcrumbs.tsx) links to each level, a
 * refresh keeps the page, and Back/Forward work. The rail is flat — section
 * headings are labels that link to their section page, not accordions —
 * and is hidden below `md`, where the home and section pages are the
 * navigation and the breadcrumb collapses to a back link.
 */
export default function AdminModule({ onAiSaved }: AdminModuleProps) {
  const { user } = useAuth();
  const route = useAdminRoute();
  const path = useAdminPath();
  const visible = useMemo(() => visibleAdminSettings(user?.modules, user?.role), [user]);
  // "Needs attention" (phase 4). Refetched on every move within Admin, so a
  // queue cleared on one screen is no longer counted on the next.
  const attention = useAdminAttention();
  const { refetch: refetchAttention } = attention;
  useEffect(() => { refetchAttention(); }, [path, refetchAttention]);
  const attentionItems = (attention.data?.items ?? []).filter((i) => visible.some((s) => s.id === i.setting));
  const counts = attentionBySetting(attentionItems);
  const [recentIds, setRecentIds] = useState<string[]>(loadRecent);

  const section = findSection(route.section ?? undefined);
  const setting = findSetting(route.setting ?? undefined);

  // A known setting under the wrong section — an old bookmark from before the
  // 2026-10-20 regrouping, or a hand-typed URL — is corrected in place rather
  // than shown as "not found".
  useEffect(() => {
    if (setting && route.section !== setting.section) navigateAdmin(settingPath(setting), { replace: true });
  }, [setting, route.section]);

  const canOpen = !!setting && visible.some((s) => s.id === setting.id);
  useEffect(() => {
    if (setting && canOpen) setRecentIds(pushRecent(setting.id));
  }, [setting, canOpen]);

  if (visible.length === 0) {
    return (
      <div className="fade-in rounded-xl border border-border bg-card p-8 text-center text-sm text-ink-muted">
        Your role does not have any Admin Panel settings assigned yet.
      </div>
    );
  }

  let page: ReactNode;
  if (!route.section) {
    page = <AdminHome visible={visible} recentIds={recentIds} attention={attentionItems} counts={counts} />;
  } else if (!section) {
    page = <NotFound what={route.section} />;
  } else if (!route.setting) {
    const items = visible.filter((s) => s.section === section.id);
    page = items.length
      ? <SectionPage sectionId={section.id} items={items} counts={counts} />
      : <NoAccess label={section.label} />;
  } else if (!setting) {
    page = <NotFound what={route.setting} />;
  } else if (!canOpen) {
    page = <NoAccess label={setting.label} grant={setting.reviewableBy ? `the ${setting.reviewableBy} role` : setting.module} />;
  } else {
    page = (
      <>
        <PageHeader setting={setting} />
        {renderSetting(setting.id, onAiSaved)}
      </>
    );
  }

  return (
    <div className="fade-in flex items-start gap-5">
      <AdminRail visible={visible} activeId={canOpen ? setting?.id : undefined} onHome={!route.section} counts={counts} />
      <main className="min-w-0 flex-1">{page}</main>
    </div>
  );
}

function renderSetting(id: string, onAiSaved?: (cfg: unknown) => void): ReactNode {
  switch (id) {
    case 'users': return <UsersRolesTab />;
    case 'merges': return <PatientMergesTab />;
    case 'specialties': return <SpecialtiesTab />;
    case 'intake': return <IntakeFormConfiguratorTab />;
    case 'consent': return <ConsentFormsTab />;
    case 'form-customization': return <FormCustomizationTab />;
    case 'clinical-library': return <ReferenceLibraryTab />;
    case 'ailments': return <AilmentTemplatesTab />;
    case 'ai-role': return <AIAssistantRolesTab />;
    case 'emergency': return <EmergencyAdminTab />;
    case 'branches': return <BranchesTab />;
    case 'pharmacy-stores': return <PharmacyStoresTab />;
    case 'wards': return <WardsBedsTab />;
    case 'availability': return <DoctorAvailabilityTab />;
    case 'queue-priority': return <QueuePriorityTab />;
    case 'drug-master': return <DrugMasterTab />;
    case 'drug-review': return <DrugReviewTab />;
    case 'formulary': return <FormularyTab />;
    case 'pharmacy-policies': return <PharmacyTab />;
    case 'laboratory': return <LabMastersTab />;
    case 'billing-settings': return <BillingSettingsTab />;
    case 'opd-fees': return <OpdFeeScheduleTab />;
    case 'insurers': return <InsuranceClaimsTab />;
    case 'messaging': return <SmsGatewayTab />;
    case 'ai-settings': return <AISettingsTab onSaved={onAiSaved} />;
    case 'ai-call-log': return <AICallLogTab />;
    case 'ai-activity': return <AIActivityTab />;
    case 'branding': return <BrandingSettingsTab />;
    case 'storage': return <StorageSettingsTab />;
    case 'background-jobs': return <BackgroundJobsTab />;
    case 'data-privacy': return <DataPrivacyTab />;
    case 'design-system': return <DesignSystemTab />;
    default: return null;
  }
}

// ── Rail ────────────────────────────────────────────────────────────────

function AdminRail({ visible, activeId, onHome, counts }: { visible: AdminSetting[]; activeId?: string; onHome: boolean; counts: AttentionCounts }) {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const query = search.trim();
  const matches = query ? visible.filter((s) => settingMatches(s, query)) : visible;

  // "/" focuses the box from anywhere in the Admin Panel, unless the key is
  // being typed into a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const openFirst = () => {
    const first = matches[0];
    if (!first) return;
    navigateAdmin(settingPath(first));
    setSearch('');
    inputRef.current?.blur();
  };

  return (
    <aside className="sticky top-0 hidden max-h-[calc(100vh-112px)] w-[256px] shrink-0 flex-col overflow-hidden rounded-xl border border-border bg-card md:flex">
      <div className="border-b border-border p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); openFirst(); }
              if (e.key === 'Escape') setSearch('');
            }}
            placeholder="Find a setting"
            aria-label="Find a setting"
            className="pl-8 pr-8 text-[13px]"
          />
          {query ? (
            // A visible way out of a search — without it the rail looks like
            // it lost half its settings.
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : (
            <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border px-1.5 font-mono text-[10px] text-muted-foreground">/</kbd>
          )}
        </div>
        {query && (
          <div className="mt-2 text-[11px] text-muted-foreground">
            {matches.length === 0 ? 'No settings match' : `${matches.length} match · Enter opens the first`}
          </div>
        )}
      </div>

      <nav aria-label="Admin settings" className="flex-1 overflow-y-auto px-2 pb-4 pt-2">
        {!query && (
          <AdminLink to={ADMIN_ROOT} aria-current={onHome ? 'page' : undefined} className={railItemClass(onHome)}>
            <span aria-hidden className={railBarClass(onHome)} />
            <House aria-hidden className={cn('h-4 w-4 shrink-0', onHome ? 'text-teal' : 'text-muted-foreground')} />
            Overview
          </AdminLink>
        )}
        {ADMIN_SECTIONS.map((sec) => {
          const items = matches.filter((s) => s.section === sec.id);
          if (items.length === 0) return null;
          return (
            <div key={sec.id}>
              <AdminLink
                to={sectionPath(sec.id)}
                className="block px-2.5 pb-1 pt-4 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground no-underline transition-colors hover:text-teal"
              >
                {sec.label}
              </AdminLink>
              {items.map((s) => {
                const active = s.id === activeId;
                const Icon = s.icon;
                return (
                  <AdminLink
                    key={s.id}
                    to={settingPath(s)}
                    title={s.description}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => setSearch('')}
                    className={railItemClass(active)}
                  >
                    <span aria-hidden className={railBarClass(active)} />
                    <Icon aria-hidden className={cn('h-4 w-4 shrink-0', active ? 'text-teal' : 'text-muted-foreground')} />
                    <span className="truncate">{s.label}</span>
                    <CountPill entry={counts.get(s.id)} />
                  </AdminLink>
                );
              })}
            </div>
          );
        })}
        {query && matches.length === 0 && (
          <div className="px-2 py-6 text-center">
            <div className="text-[12.5px] text-ink-muted">No settings match “{query}”.</div>
            <button
              type="button"
              onClick={() => setSearch('')}
              className="mt-2 text-[12px] font-semibold text-teal underline-offset-2 hover:underline"
            >
              Clear search
            </button>
          </div>
        )}
      </nav>
    </aside>
  );
}

// Active reads as a tinted row with a teal marker, not a solid fill — a filled
// row competes with the page's own primary button for "the one teal thing".
const railItemClass = (active: boolean) => cn(
  'relative flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13px] no-underline transition-colors',
  active ? 'bg-accent font-semibold text-teal' : 'text-ink-muted hover:bg-muted hover:text-ink'
);
const railBarClass = (active: boolean) => cn(
  'absolute -left-2 top-1.5 bottom-1.5 w-[3px] rounded-full', active ? 'bg-teal' : 'bg-transparent'
);

// ── Pages ───────────────────────────────────────────────────────────────

function Heading({ title, description, children }: { title: ReactNode; description: string; children?: ReactNode }) {
  return (
    <div className="mb-5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <h1 className="text-[21px] font-semibold leading-tight text-ink">{title}</h1>
        {children}
      </div>
      <p className="mt-1.5 max-w-[80ch] text-[13px] leading-relaxed text-ink-muted">{description}</p>
    </div>
  );
}

/**
 * One header per setting — the section is already in the breadcrumb, so it is
 * not repeated here, and the grant id (a support detail for the Admin working
 * out why someone can't see this page) sits behind ⓘ instead of on the page.
 */
function PageHeader({ setting }: { setting: AdminSetting }) {
  return (
    <Heading title={setting.label} description={setting.description}>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Who can open this setting"
              className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            >
              <Info className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-[280px] leading-relaxed">
            {setting.reviewableBy
              ? <>Shown to the <b>{setting.reviewableBy}</b> role.</>
              : <>Access: <span className="font-mono">{setting.module}</span>{setting.alsoModules?.length ? <> or <span className="font-mono">{setting.alsoModules.join(', ')}</span></> : null}. Granted in Users &amp; Roles.</>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {setting.reviewableBy && <Badge variant="warn">Approval: {setting.reviewableBy}</Badge>}
    </Heading>
  );
}

function AdminHome({ visible, recentIds, attention, counts }: { visible: AdminSetting[]; recentIds: string[]; attention: AttentionItem[]; counts: AttentionCounts }) {
  const recent = recentIds
    .map((id) => visible.find((s) => s.id === id))
    .filter((s): s is AdminSetting => !!s)
    .slice(0, 4);
  const sections = ADMIN_SECTIONS.filter((sec) => visible.some((s) => s.section === sec.id));

  return (
    <>
      <Heading
        title="Admin"
        description="Hospital-wide settings. Changes here apply at every branch unless a setting says otherwise."
      />
      {attention.length > 0 && <NeedsAttention items={attention} />}
      {recent.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <span className="text-[12px] font-semibold text-muted-foreground">Recently used</span>
          {recent.map((s) => {
            const Icon = s.icon;
            return (
              <AdminLink
                key={s.id}
                to={settingPath(s)}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-[12px] text-ink-muted no-underline transition-colors hover:border-teal hover:text-teal"
              >
                <Icon aria-hidden className="h-3.5 w-3.5" />
                {s.label}
              </AdminLink>
            );
          })}
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
        {sections.map((sec) => {
          const SecIcon = sec.icon;
          const items = visible.filter((s) => s.section === sec.id);
          return (
            <section key={sec.id} className="rounded-xl border border-border bg-card p-4 pb-2 shadow-card">
              <div className="mb-1 flex items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent text-teal">
                  <SecIcon aria-hidden className="h-4 w-4" />
                </span>
                <h2 className="text-[14px] font-semibold">
                  <AdminLink to={sectionPath(sec.id)} className="text-ink no-underline hover:text-teal hover:underline underline-offset-2">
                    {sec.label}
                  </AdminLink>
                </h2>
              </div>
              <p className="mb-2 text-[12px] leading-relaxed text-ink-muted">{sec.description}</p>
              <ul className="m-0 list-none border-t border-border p-0">
                {items.map((s) => {
                  const Icon = s.icon;
                  return (
                    <li key={s.id} className="border-b border-border/60 last:border-b-0">
                      <AdminLink
                        to={settingPath(s)}
                        className="group flex items-center gap-2.5 py-2 text-[13px] text-ink-muted no-underline transition-colors hover:text-teal"
                      >
                        <Icon aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-teal" />
                        <span className="truncate">{s.label}</span>
                        <CountPill entry={counts.get(s.id)} />
                        <ChevronRight aria-hidden className={cn('h-4 w-4 shrink-0 text-muted-foreground', !counts.get(s.id) && 'ml-auto')} />
                      </AdminLink>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </>
  );
}

function SectionPage({ sectionId, items, counts }: { sectionId: string; items: AdminSetting[]; counts: AttentionCounts }) {
  const sec = findSection(sectionId);
  if (!sec) return null;
  return (
    <>
      <Heading title={sec.label} description={sec.description} />
      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
        {items.map((s) => {
          const Icon = s.icon;
          return (
            <AdminLink
              key={s.id}
              to={settingPath(s)}
              className="flex items-center gap-3.5 border-b border-border px-4 py-3.5 no-underline transition-colors last:border-b-0 hover:bg-muted/50"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-ink-muted">
                <Icon aria-hidden className="h-[18px] w-[18px]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-semibold text-ink">{s.label}</span>
                <span className="block text-[12.5px] leading-snug text-ink-muted">{s.description}</span>
              </span>
              {s.reviewableBy && <Badge variant="warn" className="shrink-0 max-sm:hidden">Approval: {s.reviewableBy}</Badge>}
              <CountPill entry={counts.get(s.id)} className="ml-0" />
              <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
            </AdminLink>
          );
        })}
      </div>
    </>
  );
}

function NoAccess({ label, grant }: { label: string; grant?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-8 text-center">
      <ShieldAlert aria-hidden className="mx-auto mb-2 h-7 w-7 text-muted-foreground" />
      <div className="text-[14px] font-semibold text-ink">You don’t have access to {label}</div>
      <p className="mx-auto mt-1 max-w-[52ch] text-[13px] text-ink-muted">
        {grant
          ? <>Ask an Admin to grant <span className="font-mono">{grant}</span> to your role in Users &amp; Roles.</>
          : 'None of the settings in this section are assigned to your role.'}
      </p>
      <AdminLink to={ADMIN_ROOT} className="mt-3 inline-block text-[13px] font-semibold text-teal">Back to Admin</AdminLink>
    </div>
  );
}

function NotFound({ what }: { what: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-8 text-center">
      <div className="text-[14px] font-semibold text-ink">There is no setting called “{what}”</div>
      <p className="mt-1 text-[13px] text-ink-muted">The link may be out of date.</p>
      <AdminLink to={ADMIN_ROOT} className="mt-3 inline-block text-[13px] font-semibold text-teal">Back to Admin</AdminLink>
    </div>
  );
}

// ── Needs attention (phase 4) ───────────────────────────────────────────

type AttentionCounts = ReturnType<typeof attentionBySetting>;

/** A setting's outstanding count, coloured by the most serious item behind it. */
function CountPill({ entry, className }: { entry?: { count: number; tone: AttentionItem['tone'] }; className?: string }) {
  if (!entry) return null;
  return (
    <span
      aria-label={`${entry.count} need${entry.count === 1 ? 's' : ''} attention`}
      className={cn('ml-auto shrink-0 rounded-full px-1.5 text-[10.5px] font-bold leading-[17px]', ATTENTION_PILL[entry.tone], className)}
    >
      {entry.count > 99 ? '99+' : entry.count}
    </span>
  );
}

const TONE_ICON = { danger: AlertTriangle, warn: Clock, approval: Stamp } as const;

/**
 * The Admin home's strip: one card per item, each linking straight to the
 * setting (and tab) that resolves it. Items come already filtered to what
 * this user can open — the backend applies each setting's own grant or role.
 */
function NeedsAttention({ items }: { items: AttentionItem[] }) {
  return (
    <section aria-label="Needs attention" className="mb-6">
      <h2 className="mb-2 text-[13px] font-semibold text-ink">Needs attention</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
        {items.map((i) => {
          const s = findSetting(i.setting);
          if (!s) return null;
          const Icon = TONE_ICON[i.tone];
          return (
            <AdminLink
              key={`${i.setting}-${i.tab ?? ''}-${i.title}`}
              to={i.tab ? `${settingPath(s)}/${i.tab}` : settingPath(s)}
              className="flex items-start gap-3 rounded-xl border border-border bg-card p-3.5 no-underline shadow-card transition-colors hover:border-teal"
            >
              <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', ATTENTION_PILL[i.tone])}>
                <Icon aria-hidden className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold leading-snug text-ink">{i.title}</span>
                <span className="mt-0.5 block text-[12px] leading-snug text-ink-muted">{s.label} · {i.detail}</span>
              </span>
            </AdminLink>
          );
        })}
      </div>
    </section>
  );
}

// ── Recently used (per browser) ─────────────────────────────────────────

const RECENT_KEY = 'hms_admin_recent_tabs';

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function pushRecent(id: string): string[] {
  const next = [id, ...loadRecent().filter((x) => x !== id)].slice(0, 6);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable (private browsing, quota) — the chips just
    // stay empty next load; nothing else depends on this.
  }
  return next;
}
